import hybridApiClient from './hybridApiClient';
import { hybridApiBaseUrl, hybridFlags } from './hybridFlags';
import {
  OPTIONAL_HYBRID_ENDPOINT_KEYS,
  noteOptionalHybridEndpoint404,
  shouldSkipOptionalHybridEndpoint,
} from './optionalHybridEndpoints';
import { Device } from '../types/typesDevices';
import { Customer } from '../types/typesCustomer';
import { Repair } from '../types/typesRepair';
import { Store } from '../types/typesStores';

type CustomersPageResult = { total: number | null; hasMore?: boolean; data: Customer[] };
type LabSummaryTemplateEntry = {
  text: string;
  usageCount: number;
  lastUsedAt: string;
};

function isHybridListFetchFailure(error: any): boolean {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.code || '');
  const msg = String(error?.message || '');
  return (
    status === 401 ||
    status === 404 ||
    status === 500 ||
    code === 'ECONNABORTED' ||
    error?.message === 'Network Error' ||
    /\btimeout\b/i.test(msg) ||
    msg.includes('exceeded')
  );
}
const pendingCustomersPageRequests = new Map<string, Promise<CustomersPageResult>>();
const resolvedCustomersPageCache = new Map<string, { value: CustomersPageResult; cachedAt: number }>();
const CUSTOMERS_PAGE_CACHE_TTL_MS = 8000;

/**
 * חנויות/דגמים הם קטלוג שמשתנה נדיר — מטמון קצר + dedup לבקשות במקביל חוסך
 * עשרות קריאות כפולות (SyncService + עמודים שונים + ServiceCallsFilter) בכל ניווט.
 */
const CATALOG_CACHE_TTL_MS = 30000;
let storesCache: { value: Store[]; cachedAt: number } | null = null;
let storesInFlight: Promise<Store[]> | null = null;
let devicesCache: { value: Device[]; cachedAt: number } | null = null;
let devicesInFlight: Promise<Device[]> | null = null;

export function invalidateStoresCache(): void {
  storesCache = null;
}

export function invalidateDevicesCache(): void {
  devicesCache = null;
}
type DashboardStatsResult = Record<string, unknown>;
const pendingDashboardStatsRequests = new Map<string, Promise<DashboardStatsResult>>();
const resolvedDashboardStatsCache = new Map<string, { value: DashboardStatsResult; cachedAt: number }>();
const DASHBOARD_STATS_CACHE_TTL_MS = 8000;
type DashboardSummaryStateResult = {
  dashboard?: {
    refreshedAt?: string | null;
    refreshedAtMs?: number;
    version?: number;
    isRefreshing?: boolean;
    stale?: boolean;
    ttlMs?: number;
    lastError?: string | null;
  };
  rollups?: {
    refreshedAt?: string | null;
    refreshedAtMs?: number;
    version?: number;
    isRefreshing?: boolean;
    stale?: boolean;
    ttlMs?: number;
    lastError?: string | null;
  };
  combinedVersion?: string;
  generatedAt?: string;
};
let dashboardSummaryStateUnsupportedUntilMs = 0;

const toLightCustomer = (row: any): Customer => {
  const firstName = String(row?.firstName || '').trim();
  const lastName = String(row?.lastName || '').trim();
  return {
    id: String(row?.id || ''),
    firstName,
    lastName,
    name: String(row?.name || `${firstName} ${lastName}`.trim()),
    phone: String(row?.phone || ''),
    email: String(row?.email || ''),
    city: String(row?.city || ''),
    store: String(row?.store || ''),
    company: String(row?.company || ''),
    active: row?.active !== undefined ? Boolean(row.active) : true,
    lastActionDate: String(row?.lastActionDate || ''),
    createdAt: String(row?.createdAt || ''),
    updatedAt: String(row?.updatedAt || ''),
  } as Customer;
};

export async function fetchDevicesFromHybrid(options?: { force?: boolean }): Promise<Device[]> {
  if (!options?.force && devicesCache && Date.now() - devicesCache.cachedAt < CATALOG_CACHE_TTL_MS) {
    return devicesCache.value;
  }
  if (!options?.force && devicesInFlight) {
    return devicesInFlight;
  }
  const request = (async (): Promise<Device[]> => {
    try {
      const response: any = await hybridApiClient.get('/api/devices');
      const value = (response.data?.data || []) as Device[];
      devicesCache = { value, cachedAt: Date.now() };
      return value;
    } catch (error: any) {
      if (isHybridListFetchFailure(error)) {
        const status = Number(error?.response?.status || 0);
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] /api/devices — מחזיר רשימה ריקה (401/404/רשת/timeout).', {
          hybridApiBaseUrl,
          status,
          code: error?.code,
        });
        return [];
      }
      throw error;
    } finally {
      devicesInFlight = null;
    }
  })();
  devicesInFlight = request;
  return request;
}

export async function saveDeviceToHybrid(device: Device): Promise<Device> {
  invalidateDevicesCache();
  if (device.id) {
    const response = await hybridApiClient.patch(`/api/devices/${device.id}`, device);
    return response.data as Device;
  }
  const response = await hybridApiClient.post('/api/devices', device);
  return response.data as Device;
}

export async function deleteDeviceFromHybrid(deviceId: string): Promise<void> {
  invalidateDevicesCache();
  await hybridApiClient.delete(`/api/devices/${deviceId}`);
}

export async function fetchCustomersFromHybrid(): Promise<Customer[]> {
  try {
    const response: any = await hybridApiClient.get('/api/customers');
    return (response.data?.data || []) as Customer[];
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || status === 404 || error?.message === 'Network Error') {
      // eslint-disable-next-line no-console
      console.warn('[Hybrid API] /api/customers — מחזיר רשימה ריקה (401/404 או רשת).', { hybridApiBaseUrl, status });
      return [];
    }
    throw error;
  }
}

export async function fetchCustomerByIdFromHybrid(customerId: string): Promise<Customer | null> {
  const id = String(customerId || '').trim();
  if (!id) return null;
  try {
    const response: any = await hybridApiClient.get(`/api/customers/${id}`);
    const payload = response?.data || {};
    return toLightCustomer(payload);
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || status === 403 || status === 404 || error?.message === 'Network Error') {
      return null;
    }
    throw error;
  }
}

const customerByPhoneCache = new Map<string, { value: Customer | null; cachedAt: number }>();
const customerByPhoneInFlight = new Map<string, Promise<Customer | null>>();
const CUSTOMER_BY_PHONE_CACHE_TTL_MS = 60_000;
const OPTIONAL_ENDPOINT_UNSUPPORTED_MS = 24 * 60 * 60 * 1000;
let lookupByPhoneUnsupportedUntilMs = 0;
let customerHistoryUnsupportedUntilMs = 0;

function isLookupByPhoneEndpointUnsupported(): boolean {
  return (
    Date.now() < lookupByPhoneUnsupportedUntilMs ||
    shouldSkipOptionalHybridEndpoint(OPTIONAL_HYBRID_ENDPOINT_KEYS.lookupByPhone)
  );
}

function isCustomerHistoryEndpointUnsupported(): boolean {
  return (
    Date.now() < customerHistoryUnsupportedUntilMs ||
    shouldSkipOptionalHybridEndpoint(OPTIONAL_HYBRID_ENDPOINT_KEYS.customerHistory)
  );
}

async function fetchCustomerRepairHistoryLegacy(
  customerId: string,
  phoneSuffix: string,
  legacyFetchOpts: {
    limit: number;
    lite: boolean;
    includeTotal: false;
    signal?: AbortSignal;
    timeoutMs?: number;
  },
): Promise<Repair[]> {
  const queries: Promise<Repair[]>[] = [];
  if (customerId) {
    queries.push(fetchRepairsFromHybrid({ ...legacyFetchOpts, customerId }).then((r) => r.data || []));
  }
  if (phoneSuffix) {
    queries.push(
      fetchRepairsFromHybrid({ ...legacyFetchOpts, customerPhoneNormalized: phoneSuffix }).then((r) => r.data || []),
    );
    queries.push(fetchRepairsFromHybrid({ ...legacyFetchOpts, q: phoneSuffix }).then((r) => r.data || []));
  }
  if (queries.length === 0) return [];
  const pages = await Promise.all(queries);
  const merged = new Map<string, Repair>();
  pages.flat().forEach((repair) => {
    if (repair?.id && repairMatchesCustomer(repair, customerId, phoneSuffix)) {
      merged.set(String(repair.id), repair);
    }
  });
  return Array.from(merged.values());
}

function customerPhoneCacheKey(phone: string): string {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length >= 9) return digits.slice(-9);
  return digits;
}

function buildPhoneSearchQueries(phone: string): string[] {
  const raw = String(phone || '').trim();
  const digits = raw.replace(/\D/g, '');
  const variants = new Set<string>();
  if (raw) variants.add(raw);
  if (digits) variants.add(digits);
  if (digits.startsWith('0') && digits.length >= 10) variants.add(`972${digits.slice(1)}`);
  if (digits.startsWith('972') && digits.length >= 12) variants.add(`0${digits.slice(3)}`);
  if (digits.length >= 9) variants.add(digits.slice(-9));
  return Array.from(variants).filter(Boolean);
}

function customersPhoneMatch(left: string, right: string): boolean {
  const a = customerPhoneCacheKey(left);
  const b = customerPhoneCacheKey(right);
  return !!a && !!b && a.length >= 9 && a === b;
}

async function lookupCustomerByPhoneLegacyList(
  phone: string,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Customer | null> {
  for (const query of buildPhoneSearchQueries(phone)) {
    const response = await fetchCustomersPageFromHybrid({
      q: query,
      limit: 30,
      offset: 0,
      includeTotal: false,
      signal: options?.signal,
      timeoutMs: options?.timeoutMs ?? 4000,
    });
    const hit = (response.data || []).find((c) => customersPhoneMatch(String(c.phone || ''), phone));
    if (hit) return hit;
  }
  return null;
}

export async function fetchCustomerByPhoneFromHybrid(
  phone: string,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Customer | null> {
  const cacheKey = customerPhoneCacheKey(phone);
  if (!cacheKey || cacheKey.length < 9) return null;

  const now = Date.now();
  const cached = customerByPhoneCache.get(cacheKey);
  if (cached && now - cached.cachedAt < CUSTOMER_BY_PHONE_CACHE_TTL_MS) {
    return cached.value;
  }

  const inFlight = customerByPhoneInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const request = (async (): Promise<Customer | null> => {
    try {
      if (isLookupByPhoneEndpointUnsupported()) {
        const legacy = await lookupCustomerByPhoneLegacyList(phone, options);
        customerByPhoneCache.set(cacheKey, { value: legacy, cachedAt: Date.now() });
        return legacy;
      }

      const response: any = await hybridApiClient.get('/api/customers/lookup-by-phone', {
        params: { phone },
        signal: options?.signal,
        timeout: options?.timeoutMs ?? 2500,
        validateStatus: (status: number) => status < 500,
      });
      if (response.status === 404) {
        lookupByPhoneUnsupportedUntilMs = Date.now() + OPTIONAL_ENDPOINT_UNSUPPORTED_MS;
        noteOptionalHybridEndpoint404(OPTIONAL_HYBRID_ENDPOINT_KEYS.lookupByPhone);
        const legacy = await lookupCustomerByPhoneLegacyList(phone, options);
        customerByPhoneCache.set(cacheKey, { value: legacy, cachedAt: Date.now() });
        return legacy;
      }
      const row = response?.data?.data;
      const customer = row?.id ? toLightCustomer(row) : null;
      customerByPhoneCache.set(cacheKey, { value: customer, cachedAt: Date.now() });
      return customer;
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 401 || error?.message === 'Network Error' || error?.code === 'ECONNABORTED') {
        return null;
      }
      throw error;
    } finally {
      customerByPhoneInFlight.delete(cacheKey);
    }
  })();

  customerByPhoneInFlight.set(cacheKey, request);
  return request;
}

export function invalidateCustomerByPhoneCache(): void {
  customerByPhoneCache.clear();
}

/** שם/פרטים מהקריאה האחרונה לטלפון — לפני קטלוג לקוחות (אותו מספר, שמות שונים לאורך זמן). */
export async function fetchLatestRepairCustomerHintByPhoneFromHybrid(
  phone: string,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Customer | null> {
  const digits = String(phone || '').replace(/\D/g, '');
  const suffix = digits.length >= 9 ? digits.slice(-9) : '';
  if (suffix.length < 9) return null;

  try {
    const response: any = await hybridApiClient.get('/api/repairs/latest-by-phone', {
      params: { phone: digits },
      signal: options?.signal,
      timeout: options?.timeoutMs ?? 3000,
      validateStatus: (status: number) => status < 500,
    });
    const row = response?.data?.data;
    if (!row) return null;
    const fullName = String(row.customerName || '').trim();
    if (!fullName && !row.customerId) return null;
    const nameParts = fullName ? fullName.split(/\s+/).filter(Boolean) : [];
    return {
      id: String(row.customerId || '').trim(),
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      name: fullName,
      company: String(row.customerCompany || '').trim(),
      phone: String(row.customerPhone || phone).trim(),
      email: '',
      city: '',
      active: true,
      createdAt: String(row.createdAt || new Date().toISOString()),
      updatedAt: String(row.updatedAt || new Date().toISOString()),
    } as Customer;
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || error?.message === 'Network Error' || error?.code === 'ECONNABORTED') {
      return null;
    }
    throw error;
  }
}

function repairMatchesCustomer(repair: Repair, customerId: string, phoneSuffix: string): boolean {
  if (customerId && String(repair.customerId || '') === customerId) return true;
  if (!phoneSuffix) return false;
  const repairPhone = String(repair.customerPhoneNormalized || repair.customerPhone || '').replace(/\D/g, '');
  const repairSuffix = repairPhone.length >= 9 ? repairPhone.slice(-9) : repairPhone;
  return repairSuffix === phoneSuffix;
}

export async function fetchCustomerRepairHistoryFromHybrid(params: {
  customerId?: string;
  customerPhone?: string;
  limit?: number;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<Repair[]> {
  const customerId = String(params.customerId || '').trim();
  const phoneDigits = String(params.customerPhone || '').replace(/\D/g, '');
  const phoneSuffix = phoneDigits.length >= 9 ? phoneDigits.slice(-9) : '';
  if (!customerId && phoneSuffix.length < 9) return [];

  const limit = params.limit ?? 50;
  const legacyFetchOpts = {
    limit,
    lite: true,
    includeTotal: false as const,
    signal: params.signal,
    timeoutMs: params.timeoutMs ?? 5000,
  };

  try {
    if (isCustomerHistoryEndpointUnsupported()) {
      return fetchCustomerRepairHistoryLegacy(customerId, phoneSuffix, legacyFetchOpts);
    }

    const response: any = await hybridApiClient.get('/api/repairs/customer-history', {
      params: {
        customerId: customerId || undefined,
        customerPhoneNormalized: phoneSuffix || undefined,
        limit,
        lite: true,
      },
      signal: params.signal,
      timeout: params.timeoutMs ?? 5000,
      validateStatus: (status: number) => status < 500,
    });
    if (response.status === 404) {
      customerHistoryUnsupportedUntilMs = Date.now() + OPTIONAL_ENDPOINT_UNSUPPORTED_MS;
      noteOptionalHybridEndpoint404(OPTIONAL_HYBRID_ENDPOINT_KEYS.customerHistory);
      return fetchCustomerRepairHistoryLegacy(customerId, phoneSuffix, legacyFetchOpts);
    }
    const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
    return rows as Repair[];
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || error?.message === 'Network Error' || error?.code === 'ECONNABORTED') {
      return [];
    }
    throw error;
  }
}

export async function fetchRepairByIdFromHybrid(repairId: string): Promise<Repair | null> {
  const id = String(repairId || '').trim();
  if (!id) return null;
  try {
    const response: any = await hybridApiClient.get(`/api/repairs/${id}`);
    const payload = response?.data || {};
    return { id: String(payload.id || id), ...(payload || {}) } as Repair;
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || status === 404 || error?.message === 'Network Error') {
      return null;
    }
    throw error;
  }
}

export async function fetchCustomersPageFromHybrid(params: {
  limit?: number;
  offset?: number;
  q?: string;
  includeTotal?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<CustomersPageResult> {
  const requestKey = JSON.stringify({
    limit: Number(params.limit || 0),
    offset: Number(params.offset || 0),
    q: String(params.q || '').trim().toLowerCase(),
    includeTotal: Boolean(params.includeTotal),
  });
  const now = Date.now();
  const cachedResolved = resolvedCustomersPageCache.get(requestKey);
  if (cachedResolved && now - cachedResolved.cachedAt < CUSTOMERS_PAGE_CACHE_TTL_MS) {
    return cachedResolved.value;
  }
  const existingPendingRequest = pendingCustomersPageRequests.get(requestKey);
  if (existingPendingRequest) {
    return existingPendingRequest;
  }

  const requestPromise = (async (): Promise<CustomersPageResult> => {
    try {
    const requestedLimit = Math.max(Number(params.limit || 0), 0);
    const requestedOffset = Math.max(Number(params.offset || 0), 0);
    const response: any = await hybridApiClient.get('/api/customers', {
      params: {
        limit: params.limit,
        offset: params.offset,
        q: params.q,
        includeTotal: params.includeTotal,
        full: false,
        // Avoid conditional-cache 304 loops on some proxies/browsers.
        _ts: Date.now(),
      },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      signal: params.signal,
      // Customers list must fail fast to avoid a "stuck" screen.
      timeout: params.timeoutMs ?? 5000,
    });
    const rawRows = Array.isArray(response.data?.data) ? response.data.data : [];
    const backendLooksUnpaged =
      requestedLimit > 0 &&
      rawRows.length > requestedLimit + 1;
    const normalizedRows = rawRows.map((row: any) => toLightCustomer(row));
    const normalizedQuery = String(params.q || '').trim().toLowerCase();
    const queryDigits = normalizedQuery.replace(/\D/g, '');
    const locallyFilteredRows =
      backendLooksUnpaged && normalizedQuery
        ? normalizedRows.filter((customer: Customer) => {
            const phoneDigits = String(customer.phone || '').replace(/\D/g, '');
            const haystack = [
              customer.id,
              customer.firstName,
              customer.lastName,
              customer.name,
              customer.phone,
              customer.email,
              customer.city,
              customer.store,
              customer.company,
            ]
              .map((v) => String(v || '').toLowerCase())
              .join(' ');
            if (haystack.includes(normalizedQuery)) return true;
            if (queryDigits && phoneDigits.includes(queryDigits)) return true;
            return false;
          })
        : normalizedRows;
    const pagedRows = backendLooksUnpaged
      ? locallyFilteredRows.slice(requestedOffset, requestedOffset + requestedLimit)
      : locallyFilteredRows;
    const derivedTotal = backendLooksUnpaged ? locallyFilteredRows.length : null;
    const derivedHasMore = backendLooksUnpaged
      ? (requestedOffset + pagedRows.length < locallyFilteredRows.length)
      : undefined;

      return {
        total:
          typeof response.data?.total === 'number'
            ? response.data.total
            : (response.data?.total == null ? derivedTotal : Number(response.data.total || 0)),
        hasMore:
          typeof response.data?.hasMore === 'boolean'
            ? response.data.hasMore
            : derivedHasMore,
        // Keep customers list lightweight even if backend returns heavy payloads.
        data: pagedRows,
      };
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 401 || status === 404 || error?.message === 'Network Error' || error?.code === 'ECONNABORTED') {
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] /api/customers (paged) — מחזיר 0 תוצאות (401/404/רשת/timeout).', {
          hybridApiBaseUrl,
          status: status || error?.code || error?.message,
        });
        return { total: null, hasMore: false, data: [] };
      }
      throw error;
    }
  })();

  pendingCustomersPageRequests.set(requestKey, requestPromise);
  try {
    const result = await requestPromise;
    resolvedCustomersPageCache.set(requestKey, { value: result, cachedAt: Date.now() });
    return result;
  } finally {
    pendingCustomersPageRequests.delete(requestKey);
  }
}

export function invalidateCustomersPageCache(): void {
  resolvedCustomersPageCache.clear();
}

export async function saveCustomerToHybrid(customer: Customer): Promise<Customer> {
  try {
    if (customer.id) {
      const response = await hybridApiClient.patch(`/api/customers/${customer.id}`, customer);
      invalidateCustomersPageCache();
      invalidateCustomerByPhoneCache();
      return response.data as Customer;
    }
    const response = await hybridApiClient.post('/api/customers', customer);
    invalidateCustomersPageCache();
    invalidateCustomerByPhoneCache();
    return response.data as Customer;
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 409) {
      const existing = error?.response?.data?.existingCustomer;
      const existingName = String(existing?.name || `${existing?.firstName || ''} ${existing?.lastName || ''}`).trim();
      const existingPhone = String(existing?.phone || '').trim();
      const details = [existingName, existingPhone].filter(Boolean).join(' / ');
      const conflictError = new Error(
        details ? `מספר הטלפון כבר משויך ללקוח קיים: ${details}` : 'מספר הטלפון כבר קיים במערכת',
      ) as Error & { existingCustomer?: Customer; statusCode?: number };
      if (existing?.id) {
        conflictError.existingCustomer = toLightCustomer(existing);
      }
      conflictError.statusCode = 409;
      throw conflictError;
    }
    throw error;
  }
}

/** Keep keepId; re-link repairs from mergeIds and delete those customers. */
export async function mergeCustomersInHybrid(keepId: string, mergeIds: string[]): Promise<{
  keep: Customer;
  updatedRepairs: number;
  deletedCustomers: number;
}> {
  const response = await hybridApiClient.post('/api/customers/merge', {
    keepId,
    mergeIds,
  });
  invalidateCustomersPageCache();
  invalidateCustomerByPhoneCache();
  return {
    keep: response.data?.keep as Customer,
    updatedRepairs: Number(response.data?.updatedRepairs || 0),
    deletedCustomers: Number(response.data?.deletedCustomers || 0),
  };
}

/** Merge empty-phone duplicates into matching phone customers. */
export async function cleanupEmptyPhoneCustomerDuplicates(dryRun = false): Promise<{
  merged: number;
  updatedRepairs: number;
  deletedCustomers: number;
  planned?: number;
    items: Array<{
      keepId: string;
      mergeIds?: string[];
      mergeId?: string;
      label?: string;
      keepLabel?: string;
      keepPhone?: string;
      mergePhone?: string;
      repairPhones?: string[];
      matchedRepairPhone?: string;
      repairCount?: number;
      matchReason?: string;
    }>;
}> {
  const response = await hybridApiClient.post(
    '/api/customers/cleanup-empty-phone-duplicates',
    { dryRun },
    { timeout: 120000 },
  );
  if (!dryRun) {
    invalidateCustomersPageCache();
    invalidateCustomerByPhoneCache();
  }
  return {
    merged: Number(response.data?.merged || 0),
    updatedRepairs: Number(response.data?.updatedRepairs || 0),
    deletedCustomers: Number(response.data?.deletedCustomers || 0),
    planned: response.data?.planned != null ? Number(response.data.planned) : undefined,
    items: Array.isArray(response.data?.items) ? response.data.items : [],
  };
}

export async function deleteCustomerFromHybrid(customerId: string): Promise<void> {
  await hybridApiClient.delete(`/api/customers/${customerId}`);
  invalidateCustomersPageCache();
}

const emptyDashboardHybridStats: Record<string, unknown> = {
  customers: 0,
  newCustomersThisMonth: 0,
  openRepairs: 0,
  monthlyRevenue: 0,
  weeklyRepairs: 0,
  receivedToday: 0,
  receivedThisWeek: 0,
  receivedThisMonth: 0,
  weeklyRevenue: 0,
  dailyRevenue: 0,
  openRepairsAll: 0,
  openRepairsMy: 0,
  repairedThisWeek: 0,
  repairedToday: 0,
  repairedThisMonth: 0,
  readyToReturn: 0,
  repairsInLab: 0,
  repairsInTreatment: 0,
  repairsWaitingForApproval: 0,
  repairsCompleted: 0,
  repairsWaitingToReturn: 0,
  repairsReturnedToCustomer: 0,
  averagePerPaidCustomer: 0,
  paidRepairsThisMonth: 0,
  freeRepairsThisMonth: 0,
  technicianStats: [],
  serviceStats: [],
  todayCollection: { cash: 0, credit: 0, transfer: 0, invoice: 0 },
  repairsOlderThan7Days: 0,
};

/** Server-side aggregates for Dashboard (avoids loading every repair in the browser). */
export async function fetchDashboardStatsFromHybrid(params: {
  storeName?: string;
  startOfToday: string;
  startOfWeek: string;
  firstOfMonth: string;
  employeeId?: string;
  userDisplayName?: string;
  role?: string;
  force?: boolean;
}): Promise<Record<string, unknown>> {
  const normalizeIso = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    d.setMilliseconds(0);
    return d.toISOString();
  };
  const force = Boolean(params.force);
  const requestKey = JSON.stringify({
    ...(params || {}),
    startOfToday: normalizeIso(params.startOfToday),
    startOfWeek: normalizeIso(params.startOfWeek),
    firstOfMonth: normalizeIso(params.firstOfMonth),
    force: force ? Date.now() : false,
  });
  const now = Date.now();
  const cachedResolved = resolvedDashboardStatsCache.get(requestKey);
  if (!force && cachedResolved && now - cachedResolved.cachedAt < DASHBOARD_STATS_CACHE_TTL_MS) {
    return cachedResolved.value;
  }
  const existingPendingRequest = pendingDashboardStatsRequests.get(requestKey);
  if (!force && existingPendingRequest) {
    return existingPendingRequest;
  }
  const requestPromise = (async (): Promise<DashboardStatsResult> => {
    try {
      const response = await hybridApiClient.get('/api/repairs/dashboard-stats', { params, timeout: 120000 });
      return response.data as DashboardStatsResult;
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      const msg = String(error?.message || '');
      const code = String(error?.code || '');
      const isTimeoutLike =
        code === 'ECONNABORTED' || /\btimeout\b/i.test(msg) || msg.includes('exceeded');
      if (
        status === 401 ||
        status === 404 ||
        status >= 500 ||
        error?.message === 'Network Error' ||
        isTimeoutLike ||
        !error?.response
      ) {
        if (cachedResolved?.value) {
          // Keep last known-good dashboard stats to avoid flashing/resetting to zero on transient API failures.
          return cachedResolved.value as DashboardStatsResult;
        }
        // eslint-disable-next-line no-console
        console.warn(
          '[Hybrid API] /api/repairs/dashboard-stats לא זמין/נכשל — מחזיר סטטיסטיקות 0 זמניות.',
          { hybridApiBaseUrl, status: status || error?.message, code: code || null },
        );
        return { ...emptyDashboardHybridStats };
      }
      throw error;
    }
  })();
  pendingDashboardStatsRequests.set(requestKey, requestPromise);
  try {
    const result = await requestPromise;
    resolvedDashboardStatsCache.set(requestKey, { value: result, cachedAt: Date.now() });
    return result;
  } finally {
    pendingDashboardStatsRequests.delete(requestKey);
  }
}

export async function fetchDashboardSummaryStateFromHybrid(): Promise<DashboardSummaryStateResult> {
  if (Date.now() < dashboardSummaryStateUnsupportedUntilMs) {
    return {
      dashboard: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
      rollups: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
      combinedVersion: '0:0',
      generatedAt: new Date().toISOString(),
    };
  }
  try {
    /** נקודת קצה אגריגטיבית — NAS איטי; timeout ברירת־מחדל של Axios גורם ל־ECONNABORTED ולשגיאות React אם לא נבלע */
    const response = await hybridApiClient.get('/api/repairs/dashboard-summary-state', { timeout: 120000 });
    return (response?.data || {}) as DashboardSummaryStateResult;
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    const isTimeoutLike =
      code === 'ECONNABORTED' || /\btimeout\b/i.test(msg) || msg.includes('exceeded');
    if (status === 404) {
      // Older NAS API builds may not expose this endpoint yet; stop polling temporarily.
      dashboardSummaryStateUnsupportedUntilMs = Date.now() + 30 * 1000;
    }
    if (
      status === 401 ||
      status === 404 ||
      status >= 500 ||
      error?.message === 'Network Error' ||
      isTimeoutLike
    ) {
      if (isTimeoutLike && shouldLogDashboardSummarySoftFail()) {
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] dashboard-summary-state — timeout/רשת, ממשיכים עם stale בלי להפיל את הדף.', {
          hybridApiBaseUrl,
          code: code || null,
        });
      }
      return {
        dashboard: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
        rollups: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
        combinedVersion: '0:0',
        generatedAt: new Date().toISOString(),
      };
    }
    // בקשות ללא גוף תגובה (timeout, CORS, DNS וכו') — לא מפילים פולינג של הדשבורד
    if (!error?.response) {
      if (shouldLogDashboardSummarySoftFail()) {
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] dashboard-summary-state — ללא תגובת HTTP, ממשיכים עם stale.', {
          hybridApiBaseUrl,
          code: code || null,
          message: msg || null,
        });
      }
      return {
        dashboard: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
        rollups: { refreshedAt: null, refreshedAtMs: 0, version: 0, isRefreshing: false, stale: true },
        combinedVersion: '0:0',
        generatedAt: new Date().toISOString(),
      };
    }
    throw error;
  }
}

let lastDashboardSummarySoftFailLogMs = 0;
function shouldLogDashboardSummarySoftFail(): boolean {
  const now = Date.now();
  if (now - lastDashboardSummarySoftFailLogMs < 20000) return false;
  lastDashboardSummarySoftFailLogMs = now;
  return true;
}

const REPAIRS_QUERY_ARRAY_KEYS = new Set(['storeName', 'status', 'deviceModel', 'deviceId', 'paymentMethod']);
type RepairsPageResult = { total: number; data: Repair[]; hasMore?: boolean };
const pendingRepairsPageRequests = new Map<string, Promise<RepairsPageResult>>();
const resolvedRepairsPageCache = new Map<string, { value: RepairsPageResult; cachedAt: number }>();
const REPAIRS_PAGE_CACHE_TTL_WITH_TOTAL_MS = 8000;
const REPAIRS_PAGE_CACHE_TTL_NO_TOTAL_MS = 60_000;
const isRepairsFetchDebugEnabled = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('debug.repairsFetch') === '1';
  } catch {
    return false;
  }
};
const logRepairsFetchDebug = (message: string, payload?: Record<string, unknown>) => {
  if (!isRepairsFetchDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.debug(`[repairs-fetch] ${message}`, payload || {});
};

/** ערכים שעשויים להופיע כמספר פרמטרים זהים (?k=a&k=b) — לא מפצלים מחרוזת לפי פסיק */
function serializeRepairsListQueryParams(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (REPAIRS_QUERY_ARRAY_KEYS.has(key) && Array.isArray(raw)) {
      for (const item of raw) {
        const s = String(item).trim();
        if (s) sp.append(key, s);
      }
    } else if (typeof raw === 'boolean') {
      sp.append(key, raw ? 'true' : 'false');
    } else {
      sp.append(key, String(raw));
    }
  }
  return sp.toString();
}

function normalizeRepairsQueryForCache(params: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (Array.isArray(raw)) {
      const list = raw.map((v) => String(v).trim()).filter(Boolean);
      if (list.length > 0) normalized[key] = list;
      continue;
    }
    normalized[key] = typeof raw === 'string' ? raw.trim() : raw;
  }
  return normalized;
}

/** null = עדיין לא ידוע, false = קיבלנו 404 (endpoint לא קיים בגרסת השרת הפרוסה) */
let systemTotalEndpointAvailable: boolean | null = null;
const systemTotalInFlight = new Map<string, Promise<number>>();

export async function fetchRepairsSystemTotalFromHybrid(params?: {
  storeName?: string | string[];
}): Promise<number> {
  if (systemTotalEndpointAvailable === false) return -1;

  const queryParams: Record<string, unknown> = {};
  if (params?.storeName != null && params.storeName !== '') {
    queryParams.storeName = params.storeName;
  }
  const key = JSON.stringify(queryParams);
  const existing = systemTotalInFlight.get(key);
  if (existing) return existing;

  const req = (async (): Promise<number> => {
    try {
      const response: any = await hybridApiClient.get('/api/repairs/meta/system-total', {
        params: queryParams,
        timeout: 60000,
      });
      systemTotalEndpointAvailable = true;
      return Number(response.data?.total || 0);
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 404) {
        systemTotalEndpointAvailable = false;
        return -1;
      }
      throw error;
    } finally {
      systemTotalInFlight.delete(key);
    }
  })();
  systemTotalInFlight.set(key, req);
  return req;
}

export async function fetchRepairsFromHybrid(params: {
  limit?: number;
  offset?: number;
  includeTotal?: boolean;
  signal?: AbortSignal;
  storeName?: string | string[];
  /** תגובת רשימה מצומצמת (מהיר יותר) */
  lite?: boolean;
  /** כולל sparePartsWithQty גם במצב lite (לדוחות) */
  includeSpareParts?: boolean;
  /** סטטוס יחיד או רשימה (מספר סטטוסים = OR ב-SQL) */
  status?: string | string[];
  /** דגם מכשיר (שם מקטלוג) — יחיד או רשימה; תואם payload.deviceModel */
  deviceModel?: string | string[];
  /** מזהה מכשיר בקטלוג — יחיד או רשימה; תואם payload.deviceId (OR עם deviceModel בשרת) */
  deviceId?: string | string[];
  employeeId?: string;
  employeeName?: string;
  customerId?: string;
  customerPhoneNormalized?: string;
  paymentHandledById?: string;
  paymentHandledByName?: string;
  paymentHandledByIds?: string[];
  q?: string;
  beforeDate?: string;
  afterDate?: string;
  beforeDateField?: 'receivedDate' | 'createdAt' | 'technicianCompletedDate' | 'paymentDate';
  dateField?: 'receivedDate' | 'createdAt' | 'technicianCompletedDate' | 'paymentDate';
  paymentStatus?: string;
  /** החזר מלא/חלקי או ביטול לכידה */
  paymentReversalOnly?: boolean;
  /** גבייה בפועל (אשראי/לא-אשראי) — סינון בשרת, תואם לדשבורד */
  collectedOnly?: boolean;
  paymentMethodGroup?: 'credit' | 'nonCredit';
  /** אמצעי תשלום מדויק (אשראי / מזומן / חשבונית / העברה בנקאית) */
  paymentMethod?: string | string[];
  /** טווח תאריך תשלום (נפרד מתאריך קליטה) */
  paymentAfterDate?: string;
  paymentBeforeDate?: string;
  /** במעבדה מעל 14 ימים מלאים — סינון בשרת (תואם לדשבורד) */
  labOverSevenDays?: boolean;
}): Promise<RepairsPageResult> {
  const requestStart = Date.now();
  try {
    const {
      limit,
      offset,
      includeTotal,
      signal,
      storeName,
      lite,
      includeSpareParts,
      status,
      deviceModel,
      deviceId,
      employeeId,
      employeeName,
      customerId,
      customerPhoneNormalized,
      paymentHandledById,
      paymentHandledByName,
      paymentHandledByIds,
      q,
      beforeDate,
      afterDate,
      beforeDateField,
      dateField,
      paymentStatus,
      paymentReversalOnly,
      collectedOnly,
      paymentMethodGroup,
      paymentMethod,
      paymentAfterDate,
      paymentBeforeDate,
      labOverSevenDays,
    } = params;
    const queryParams: Record<string, unknown> = {};
    if (limit != null) queryParams.limit = limit;
    if (offset != null) queryParams.offset = offset;
    if (includeTotal != null) queryParams.includeTotal = Boolean(includeTotal);
    if (lite != null) queryParams.lite = Boolean(lite);
    if (includeSpareParts != null) queryParams.includeSpareParts = Boolean(includeSpareParts);
    if (storeName != null && storeName !== '') {
      if (Array.isArray(storeName)) {
        const list = storeName.map((s) => String(s).trim()).filter(Boolean);
        if (list.length > 0) queryParams.storeName = list.length === 1 ? list[0] : list;
      } else {
        queryParams.storeName = String(storeName).trim();
      }
    }
    if (employeeId) queryParams.employeeId = String(employeeId).trim();
    if (employeeName) queryParams.employeeName = String(employeeName).trim();
    if (customerId) queryParams.customerId = String(customerId).trim();
    if (customerPhoneNormalized) {
      const normalized = String(customerPhoneNormalized).replace(/\D/g, '');
      if (normalized) queryParams.customerPhoneNormalized = normalized;
    }
    if (paymentHandledById) queryParams.paymentHandledById = String(paymentHandledById).trim();
    if (paymentHandledByName) queryParams.paymentHandledByName = String(paymentHandledByName).trim();
    if (Array.isArray(paymentHandledByIds) && paymentHandledByIds.length > 0) {
      queryParams.paymentHandledByIds = paymentHandledByIds.map((id) => String(id || '').trim()).filter(Boolean);
    }
    if (q) queryParams.q = q;
    if (beforeDate) queryParams.beforeDate = String(beforeDate).trim();
    if (afterDate) queryParams.afterDate = String(afterDate).trim();
    if (beforeDateField) queryParams.beforeDateField = String(beforeDateField).trim();
    // תאימות לאחור/שרתים שונים: חלק מהשרתים משתמשים בשם dateField
    if (dateField) queryParams.dateField = String(dateField).trim();
    if (paymentStatus) queryParams.paymentStatus = String(paymentStatus).trim();
    if (paymentReversalOnly) queryParams.paymentReversalOnly = 'true';
    if (collectedOnly) queryParams.collectedOnly = 'true';
    if (paymentMethodGroup === 'credit' || paymentMethodGroup === 'nonCredit') {
      queryParams.paymentMethodGroup = paymentMethodGroup;
    }
    if (paymentMethod != null && paymentMethod !== '') {
      if (Array.isArray(paymentMethod)) {
        const list = paymentMethod.map((s) => String(s).trim()).filter(Boolean);
        if (list.length > 0) queryParams.paymentMethod = list.length === 1 ? list[0] : list;
      } else {
        queryParams.paymentMethod = String(paymentMethod).trim();
      }
    }
    if (paymentAfterDate) queryParams.paymentAfterDate = String(paymentAfterDate).trim();
    if (paymentBeforeDate) queryParams.paymentBeforeDate = String(paymentBeforeDate).trim();
    if (labOverSevenDays) queryParams.labOverSevenDays = 'true';
    if (status != null && status !== '') {
      if (Array.isArray(status)) {
        const list = status.map((s) => String(s).trim()).filter(Boolean);
        if (list.length > 0) queryParams.status = list.length === 1 ? list[0] : list;
      } else {
        queryParams.status = String(status).trim();
      }
    }
    if (deviceModel != null && deviceModel !== '') {
      if (Array.isArray(deviceModel)) {
        const list = deviceModel.map((s) => String(s).trim()).filter(Boolean);
        if (list.length > 0) queryParams.deviceModel = list.length === 1 ? list[0] : list;
      } else {
        queryParams.deviceModel = String(deviceModel).trim();
      }
    }
    if (deviceId != null && deviceId !== '') {
      if (Array.isArray(deviceId)) {
        const list = deviceId.map((s) => String(s).trim()).filter(Boolean);
        if (list.length > 0) queryParams.deviceId = list.length === 1 ? list[0] : list;
      } else {
        queryParams.deviceId = String(deviceId).trim();
      }
    }
    const normalizedForCache = normalizeRepairsQueryForCache(queryParams);
    const requestKey = serializeRepairsListQueryParams(normalizedForCache);
    const includeTotalRequested = includeTotal === true;
    const cacheTtlMs = includeTotalRequested
      ? REPAIRS_PAGE_CACHE_TTL_WITH_TOTAL_MS
      : REPAIRS_PAGE_CACHE_TTL_NO_TOTAL_MS;
    const now = Date.now();
    const cachedResolved = resolvedRepairsPageCache.get(requestKey);
    if (cachedResolved && now - cachedResolved.cachedAt < cacheTtlMs) {
      logRepairsFetchDebug('cache-hit', {
        ms: Date.now() - requestStart,
        key: requestKey,
        limit: queryParams.limit,
        offset: queryParams.offset,
      });
      return cachedResolved.value;
    }
    const existingPendingRequest = pendingRepairsPageRequests.get(requestKey);
    if (existingPendingRequest) {
      logRepairsFetchDebug('inflight-reuse', {
        ms: Date.now() - requestStart,
        key: requestKey,
        limit: queryParams.limit,
        offset: queryParams.offset,
      });
      return existingPendingRequest;
    }
    const needsCustomSerializer =
      Array.isArray(queryParams.storeName) ||
      Array.isArray(queryParams.status) ||
      Array.isArray(queryParams.deviceModel) ||
      Array.isArray(queryParams.deviceId);
    const requestPromise = (async (): Promise<RepairsPageResult> => {
      const response = await hybridApiClient.get('/api/repairs', {
        params: queryParams,
        signal,
        ...(needsCustomSerializer
          ? { paramsSerializer: { serialize: (p: Record<string, unknown>) => serializeRepairsListQueryParams(p) } }
          : {}),
      });
      return response.data as RepairsPageResult;
    })();
    pendingRepairsPageRequests.set(requestKey, requestPromise);
    try {
      const result = await requestPromise;
      const normalized: RepairsPageResult = {
        total: Number(result?.total || 0),
        data: Array.isArray(result?.data) ? result.data : [],
        hasMore: Boolean(result?.hasMore),
      };
      if (!Array.isArray(result?.data)) {
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] /api/repairs — תשובה בלי data (אולי HTML מפרוקסי).', {
          hybridApiBaseUrl,
          typeofData: typeof result?.data,
        });
      }
      resolvedRepairsPageCache.set(requestKey, { value: normalized, cachedAt: Date.now() });
      logRepairsFetchDebug('network-ok', {
        ms: Date.now() - requestStart,
        key: requestKey,
        total: normalized.total,
        rows: normalized.data.length,
        hasMore: normalized.hasMore,
      });
      return normalized;
    } finally {
      pendingRepairsPageRequests.delete(requestKey);
    }
  } catch (error: any) {
    logRepairsFetchDebug('network-error', {
      ms: Date.now() - requestStart,
      code: error?.code,
      message: error?.message,
      status: Number(error?.response?.status || 0) || undefined,
    });
    const status = Number(error?.response?.status || 0);
    const code = String(error?.code || '');
    const isTransientNetwork =
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      error?.message === 'Network Error';
    if (status === 401 || status === 404) {
      // eslint-disable-next-line no-console
      console.warn('[Hybrid API] /api/repairs — מחזיר 0 תוצאות (401/404).', { hybridApiBaseUrl, status });
      return { total: 0, data: [], hasMore: false };
    }
    throw error;
  }
}

/** איפוס מטמון עמודי רשימה (למשל אחרי שינוי סינון תשלום) */
export function invalidateRepairsHybridPageCache(): void {
  resolvedRepairsPageCache.clear();
  pendingRepairsPageRequests.clear();
}

type RepairsListQueryParams = Omit<
  Parameters<typeof fetchRepairsFromHybrid>[0],
  'limit' | 'offset' | 'includeTotal' | 'lite' | 'signal'
>;

/** COUNT בלבד לסינון — רץ ברקע אחרי שהרשימה כבר הוצגה (בלי לחסום את הטבלה) */
export async function fetchRepairsFilteredCountFromHybrid(
  params: RepairsListQueryParams,
  signal?: AbortSignal,
): Promise<number> {
  const result = await fetchRepairsFromHybrid({
    ...params,
    limit: 1,
    offset: 0,
    includeTotal: true,
    lite: true,
    signal,
  });
  return Math.max(0, Number(result.total ?? 0));
}

/** חימום מטמון רשימת קריאות ברקע אחרי התחברות — מעבר לעמוד קריאות מהיר יותר */
export function warmRepairsListOnLogin(): void {
  if (!hybridFlags.useQnapRepairs && !hybridFlags.disableFirestoreReads) return;
  void fetchRepairsFromHybrid({
    limit: 200,
    offset: 0,
    includeTotal: false,
    lite: true,
  }).catch(() => {
    /* ignore warm-up errors */
  });
}

function notifyRepairMutation(action: 'create' | 'update' | 'delete', repairId?: string) {
  try {
    invalidateRepairsHybridPageCache();
    if (typeof window !== 'undefined') {
      // איפוס חסימות זמניות כדי שסקרי הדשבורד ירוצו מיד
      dashboardSummaryStateUnsupportedUntilMs = 0;
      window.dispatchEvent(
        new CustomEvent('repair-mutated', { detail: { action, repairId } }),
      );
    }
  } catch {
    /* noop */
  }
}

export async function markPriorityInvoiceInHybrid(repairId: string): Promise<Repair> {
  const id = String(repairId || '').trim();
  if (!id) {
    throw new Error('Missing repair id');
  }
  const existing = await fetchRepairByIdFromHybrid(id);
  if (!existing?.id) {
    throw new Error('Repair not found');
  }
  const nowIso = new Date().toISOString();
  const merged: Repair = {
    ...existing,
    priorityInvoiceIssued: true,
    priorityInvoiceIssuedAt: nowIso,
    updatedAt: nowIso,
  };
  const response = await hybridApiClient.patch(`/api/repairs/${id}`, merged);
  notifyRepairMutation('update', id);
  const payload = (response?.data || {}) as Record<string, unknown>;
  const saved = { id: String(payload.id || id), ...payload } as Repair;

  // השרת בענן עשוי לא להחזיר את השדה בתשובה — מאמתים מול GET מלא
  const issuedAt = String(saved.priorityInvoiceIssuedAt || nowIso).trim() || nowIso;
  const rawIssued = (saved as { priorityInvoiceIssued?: boolean | string | number }).priorityInvoiceIssued;
  const issuedInResponse =
    rawIssued === true || rawIssued === 1 || String(rawIssued || '').trim().toLowerCase() === 'true';
  if (!issuedInResponse) {
    const fresh = await fetchRepairByIdFromHybrid(id);
    if (fresh) {
      return { ...saved, ...fresh, priorityInvoiceIssued: true, priorityInvoiceIssuedAt: issuedAt };
    }
  }
  return { ...saved, priorityInvoiceIssued: true, priorityInvoiceIssuedAt: issuedAt };
}

export async function setCreditInvoiceIssuedInHybrid(repairId: string, issued: boolean): Promise<Repair> {
  const id = String(repairId || '').trim();
  if (!id) {
    throw new Error('Missing repair id');
  }
  const existing = await fetchRepairByIdFromHybrid(id);
  if (!existing?.id) {
    throw new Error('Repair not found');
  }
  const nowIso = new Date().toISOString();
  const merged: Repair = {
    ...existing,
    creditInvoiceIssued: issued,
    creditInvoiceIssuedAt: issued ? nowIso : existing.creditInvoiceIssuedAt,
    updatedAt: nowIso,
  };
  const response = await hybridApiClient.patch(`/api/repairs/${id}`, merged);
  notifyRepairMutation('update', id);
  const payload = (response?.data || {}) as Record<string, unknown>;
  return {
    ...merged,
    ...payload,
    id: String(payload.id || id),
    creditInvoiceIssued: issued,
  } as Repair;
}

export async function saveRepairToHybrid(repair: Repair): Promise<Repair> {
  if (repair.id) {
    const response = await hybridApiClient.patch(`/api/repairs/${repair.id}`, repair);
    notifyRepairMutation('update', repair.id);
    return response.data as Repair;
  }
  const response = await hybridApiClient.post('/api/repairs', repair);
  const saved = response.data as Repair;
  notifyRepairMutation('create', saved?.id);
  return saved;
}

export async function deleteRepairFromHybrid(repairId: string): Promise<void> {
  await hybridApiClient.delete(`/api/repairs/${repairId}`);
  notifyRepairMutation('delete', repairId);
}

export async function fetchLabSummaryTemplatesFromHybrid(): Promise<LabSummaryTemplateEntry[]> {
  const response = await hybridApiClient.get('/api/repairs/lab-summary-templates');
  const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
  return rows
    .map((row: any) => ({
      text: String(row?.text || '').replace(/\s+/g, ' ').trim(),
      usageCount: Math.max(1, Number(row?.usageCount || 0) || 1),
      lastUsedAt: String(row?.lastUsedAt || ''),
    }))
    .filter((row: LabSummaryTemplateEntry) => Boolean(row.text))
    .slice(0, 30);
}

export async function upsertLabSummaryTemplateToHybrid(
  template: LabSummaryTemplateEntry,
): Promise<LabSummaryTemplateEntry | null> {
  const text = String(template?.text || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const response = await hybridApiClient.post('/api/repairs/lab-summary-templates', {
    text,
    usageCount: Math.max(1, Number(template?.usageCount || 0) || 1),
    lastUsedAt: String(template?.lastUsedAt || ''),
  });
  const row = response?.data || {};
  return {
    text: String(row?.text || text).replace(/\s+/g, ' ').trim(),
    usageCount: Math.max(1, Number(row?.usageCount || 0) || 1),
    lastUsedAt: String(row?.lastUsedAt || new Date().toISOString()),
  };
}

export async function removeLabSummaryTemplateFromHybrid(textValue: string): Promise<void> {
  const text = String(textValue || '').replace(/\s+/g, ' ').trim();
  if (!text) return;
  await hybridApiClient.delete('/api/repairs/lab-summary-templates', { data: { text } });
}

export async function bulkMarkRepairsReturnedToCustomerInHybrid(params: {
  ids: string[];
  returnedAt?: string;
}): Promise<{ updatedIds: string[]; requested: number; updated: number }> {
  const ids = Array.isArray(params.ids) ? params.ids.map((id) => String(id || '').trim()).filter(Boolean) : [];
  if (ids.length === 0) return { updatedIds: [], requested: 0, updated: 0 };
  const response = await hybridApiClient.post('/api/repairs/bulk-mark-returned', {
    ids,
    returnedAt: params.returnedAt,
  });
  const payload = response?.data || {};
  const updatedIds = Array.isArray(payload.updatedIds)
    ? payload.updatedIds.map((id: unknown) => String(id || '').trim()).filter(Boolean)
    : [];
  notifyRepairMutation('update');
  return {
    updatedIds,
    requested: Number(payload.requested || ids.length),
    updated: Number(payload.updated || updatedIds.length),
  };
}

export async function fetchStoresFromHybrid(options?: { force?: boolean }): Promise<Store[]> {
  if (!options?.force && storesCache && Date.now() - storesCache.cachedAt < CATALOG_CACHE_TTL_MS) {
    return storesCache.value;
  }
  if (!options?.force && storesInFlight) {
    return storesInFlight;
  }
  const request = (async (): Promise<Store[]> => {
    try {
      const response: any = await hybridApiClient.get('/api/stores');
      const value = (response.data?.data || []) as Store[];
      storesCache = { value, cachedAt: Date.now() };
      return value;
    } catch (error: any) {
      if (isHybridListFetchFailure(error)) {
        const status = Number(error?.response?.status || 0);
        // לא להפיל את האפליקציה אם ה-endpoint לא זמין / NAS איטי / timeout.
        // eslint-disable-next-line no-console
        console.warn('[Hybrid API] /api/stores — מחזיר רשימה ריקה.', {
          hybridApiBaseUrl,
          status,
          code: error?.code,
        });
        return [];
      }
      throw error;
    } finally {
      storesInFlight = null;
    }
  })();
  storesInFlight = request;
  return request;
}

export async function saveStoreToHybrid(store: Store): Promise<Store> {
  invalidateStoresCache();
  if (store.id) {
    const response = await hybridApiClient.patch(`/api/stores/${store.id}`, store);
    return response.data as Store;
  }
  const response = await hybridApiClient.post('/api/stores', store);
  return response.data as Store;
}

export async function deleteStoreFromHybrid(storeId: string): Promise<void> {
  invalidateStoresCache();
  await hybridApiClient.delete(`/api/stores/${storeId}`);
}
